import React, { useState, useEffect } from "react";
import { Col, Radio, Row, Select } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

// graphql
import GET_LANGUAGES from "graphql/queries/GetLanguages";
import GET_COURSES from "graphql/queries/GetCourses";

// custom types
import { Level, Language } from "types";

// import types for antd components
import { SelectValue } from "antd/lib/select";
import { RadioChangeEvent } from "antd/lib/radio";

// custom components
import * as Layout from "common/layout";
import {
  StyledSelector,
  StyledFilter,
  SelectorContainer,
  CourseDetails,
} from "./WordpressFragmentStyle";
import CourseList from "./CourseList";

// styling
const { PageContent } = Layout;
const { Option } = Select;

type CourseFilter = {
  levelId?: number;
  languageId?: string;
  courseType?: string;
  courseDays?: string;
  courseTime?: string;
};

const WordpressFragment = () => {
  const { t } = useTranslation("book_a_course");

  const { data: languagesData } = useQuery(GET_LANGUAGES);

  const [courseList, setCourseList] = useState([]);
  const [loadingCourseList, setLoadingCourseList] = useState(false);

  const firstLanguage = languagesData?.languages.find((language: Language) => {
    return language.englishName === "Mandarin";
  });

  const [filter, setFilter] = useState<CourseFilter>({
    levelId: undefined || firstLanguage?.levels[0]?.id,
    languageId: undefined || firstLanguage?.id,
    courseType: "standard",
    courseDays: "weekdays",
    courseTime: "ongoing",
  });

  const changeFilter = (newFilter: CourseFilter) => {
    setFilter({
      ...filter,
      ...newFilter,
    });
  };

  const onLanguageFieldChange = (value: string) => {
    const studyLanguage = languagesData.languages.find((language: Language) => {
      return language.id === value;
    });

    changeFilter({
      languageId: studyLanguage.id,
      levelId: studyLanguage.levels[0]?.id,
    });
  };

  const manuallyChangeFilter = (newFilter: CourseFilter) => {
    changeFilter(newFilter);
  };

  const { data: coursesList, loading } = useQuery(GET_COURSES, {
    skip: !filter.levelId || !filter.courseType,
    variables: {
      levelId: filter.levelId,
      courseType: filter.courseType,
      courseDays: filter.courseDays,
      languageId: undefined,
    },
    fetchPolicy: "network-only",
    onCompleted: () => {
      setCourseList(coursesList.courses);
    },
  });

  useEffect(() => {
    setLoadingCourseList(loading);
  }, [loading, setLoadingCourseList]);

  useEffect(() => {
    if (coursesList && filter.courseTime === "ongoing") {
      const filteredCourseList = coursesList.courses?.filter(
        (course: any) => course.ongoing === true
      );
      setCourseList(filteredCourseList);
    } else if (coursesList && filter.courseTime === "future") {
      const filteredCourseList = coursesList.courses?.filter(
        (course: any) => course.future === true
      );
      setCourseList(filteredCourseList);
    }
  }, [filter.courseTime, coursesList, setCourseList]);

  const studyLanguage = languagesData?.languages.find(
    (language: { id: string }) => {
      return language.id === filter.languageId;
    }
  );

  return (
    <PageContent>
      <Row
        gutter={{ xs: 0, sm: 0, md: 0, lg: 12, xl: 24 }}
        style={{ marginBottom: "10px" }}
      >
        <Col xs={24} lg={5} xl={7} xxl={6}>
          <SelectorContainer>
            <StyledSelector
              suffixIcon={<CaretDownOutlined />}
              placeholder={t("Choose Language")}
              value={filter.languageId}
              optionFilterProp="children"
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value: SelectValue) => {
                onLanguageFieldChange(value as string);
              }}
              dropdownStyle={{ textAlign: "left" }}
            >
              {languagesData?.languages.map((languages: Language) => (
                <Option value={languages.id} key={languages.id}>
                  {`${languages.englishName}`}
                </Option>
              ))}
            </StyledSelector>
          </SelectorContainer>
        </Col>
        <Col xs={0} lg={19} />
      </Row>
      <Row gutter={{ xs: 0, sm: 0, md: 0, lg: 12, xl: 24 }}>
        <Col xs={24} lg={5} xl={7} xxl={6} style={{ marginBottom: "10px" }}>
          <SelectorContainer>
            <StyledSelector
              suffixIcon={<CaretDownOutlined />}
              placeholder={t("My Current Level")}
              value={filter.levelId}
              optionFilterProp="children"
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value: SelectValue) =>
                manuallyChangeFilter({
                  levelId: value as number,
                })
              }
              dropdownStyle={{ textAlign: "left" }}
            >
              {studyLanguage?.levels.map((level: Level) => (
                <Option value={level.id} key={level.id}>
                  {level.title}
                </Option>
              ))}
            </StyledSelector>
          </SelectorContainer>
        </Col>
        <Col xs={24} lg={5} xl={4} xxl={6} style={{ marginBottom: "10px" }}>
          <StyledFilter
            value={filter.courseTime}
            onChange={(ev: RadioChangeEvent) => {
              manuallyChangeFilter({
                courseTime: ev.target.value as string,
              });
            }}
          >
            <Radio.Button value="ongoing">{t("Ongoing")}</Radio.Button>
            <Radio.Button value="future">{t("Future")}</Radio.Button>
          </StyledFilter>
        </Col>
        <Col xs={24} lg={8} xl={8} xxl={6} style={{ marginBottom: "10px" }}>
          <StyledFilter
            value={filter.courseType}
            onChange={(ev: RadioChangeEvent) => {
              manuallyChangeFilter({
                courseType: ev.target.value as string,
              });
            }}
          >
            <Radio.Button value="standard">{t("Standard 4h/wk")}</Radio.Button>
            <Radio.Button value="intensive">
              {t("Intensive 10h/wk")}
            </Radio.Button>
          </StyledFilter>
        </Col>
        <Col xs={24} lg={6} xl={5} xxl={6} style={{ marginBottom: "10px" }}>
          <StyledFilter
            value={filter.courseDays}
            onChange={(ev: RadioChangeEvent) =>
              manuallyChangeFilter({
                courseDays: ev.target.value as string,
              })
            }
          >
            <Radio.Button value="weekdays">{t("Weekdays")}</Radio.Button>
            <Radio.Button value="weekends">{t("Weekends")}</Radio.Button>
          </StyledFilter>
        </Col>
      </Row>
      <CourseDetails>
        All course times are displayed in the time zone you selected in
        <a href="/account/my-settings"> My Settings.</a>
      </CourseDetails>
      <CourseList
        courseList={courseList}
        loadingCourseList={loadingCourseList}
      />
    </PageContent>
  );
};

export default WordpressFragment;
